<script setup>
import { computed, nextTick, ref, useId } from 'vue';
import CardDetailModal from './CardDetailModal.vue';
import CardPrintLink from './CardPrintLink.vue';
import { REFERENCE_FONT_SIZE_PIXELS, normalizeCard, normalizeSettings, previewText } from './cards.js';

const props = defineProps({
    items: { type: Array, default: () => [] },
    settings: { type: Object, default: () => ({}) },
    heading: { type: String, default: '' }
});
const cards = computed(() => props.items.map(normalizeCard));
const options = computed(() => normalizeSettings(props.settings));
const gridClass = computed(() => [
    `row-cols-sm-${Math.min(options.value.columns, 2)}`,
    `row-cols-lg-${options.value.columns}`
]);
const cardHeight = computed(() => `${options.value.cardHeightPixels / REFERENCE_FONT_SIZE_PIXELS}rem`);
const selectedCard = ref(null);
const popupUnavailable = ref(false);
const inlineHeading = ref(null);
const inlineTitleId = `${useId()}-inline-card-title`;
let opener;

async function openCard(card, event) {
    opener = event.currentTarget;
    // Explicit focus makes return focus consistent for pointer activation across browsers.
    opener.focus({ preventScroll: true });
    selectedCard.value = card;
    if (popupUnavailable.value) {
        await nextTick();
        inlineHeading.value?.focus();
    }
}

async function popupFailed() {
    popupUnavailable.value = true;
    await nextTick();
    inlineHeading.value?.focus();
}

function closeInline() {
    selectedCard.value = null;
    if (opener?.isConnected) opener.focus({ preventScroll: true });
}

function modalClosed() {
    if (!popupUnavailable.value) selectedCard.value = null;
}
</script>

<template>
    <section class="resource-card-grid">
        <h2 v-if="heading" class="mb-4">{{ heading }}</h2>
        <div v-if="cards.length" class="row row-cols-1 g-4" :class="gridClass">
            <div v-for="card in cards" :key="card.id" class="col">
                <article class="card shadow-sm resource-card" :style="{ height: cardHeight }">
                    <button v-if="card.imageUrl" type="button" class="card-image-button p-0 border-0 bg-transparent rounded-top"
                            :aria-label="`Read more about ${card.title}`" @click="openCard(card, $event)">
                        <img :src="card.imageUrl" :alt="card.imageAlt" class="card-img-top card-cap"
                             loading="lazy" decoding="async">
                    </button>
                    <div class="card-body d-flex flex-column card-preview-body">
                        <component :is="heading ? 'h3' : 'h2'" class="card-title h5 flex-shrink-0">
                            <button type="button" class="btn btn-link p-0 text-start text-decoration-none card-title-button"
                                    :aria-label="`Read more about ${card.title}`" @click="openCard(card, $event)">
                                {{ card.title }}
                            </button>
                        </component>
                        <p class="card-text text-break card-summary mb-0">{{ previewText(card.summary, options.previewWordLimit) }}</p>
                        <div class="pt-2 mt-auto flex-shrink-0">
                            <button type="button" class="btn btn-outline-primary" :aria-label="`Read more about ${card.title}`"
                                    @click="openCard(card, $event)">
                                <i class="bi bi-info-circle me-1" aria-hidden="true"></i>Read more
                            </button>
                        </div>
                    </div>
                </article>
            </div>
        </div>
        <p v-else class="text-body-secondary">No cards are available yet.</p>

        <CardDetailModal v-if="!popupUnavailable" :card="selectedCard" @closed="modalClosed" @error="popupFailed" />

        <!-- Keep every card's full information available if the local popup bundle fails. -->
        <section v-if="popupUnavailable && selectedCard" class="border rounded p-4 mt-4 text-break"
                 :aria-labelledby="inlineTitleId" data-card-inline-details>
            <p class="text-body-secondary" role="status">Card details are shown below.</p>
            <h2 :id="inlineTitleId" ref="inlineHeading" tabindex="-1">{{ selectedCard.title }}</h2>
            <img v-if="selectedCard.imageUrl" :src="selectedCard.imageUrl" :alt="selectedCard.imageAlt"
                 class="img-fluid rounded mb-3 inline-image" decoding="async">
            <div v-if="selectedCard.bodyHtml" class="card-rich-content" v-html="selectedCard.bodyHtml"></div>
            <p v-else>{{ selectedCard.summary }}</p>
            <div v-if="selectedCard.footerHtml" class="card-rich-content border-top pt-3 mt-3" v-html="selectedCard.footerHtml"></div>
            <div class="d-flex flex-wrap gap-2 mt-3">
                <button type="button" class="btn btn-outline-secondary" @click="closeInline">Close details</button>
                <CardPrintLink :url="selectedCard.printUrl" />
                <a v-if="selectedCard.linkUrl" :href="selectedCard.linkUrl" class="btn btn-primary">
                    {{ selectedCard.linkLabel }}<i class="bi bi-arrow-up-right ms-1" aria-hidden="true"></i>
                </a>
            </div>
        </section>
    </section>
</template>

<style scoped>
.resource-card {
    overflow: hidden;
}

.card-image-button {
    flex: 0 0 10rem;
    height: 10rem;
    overflow: hidden;
}

.card-cap {
    height: 100%;
    object-fit: cover;
}

.card-preview-body,
.card-summary {
    min-height: 0;
}

.card-preview-body {
    overflow: hidden;
}

.card-title-button {
    display: -webkit-box;
    overflow: hidden;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    font: inherit;
    max-height: 3.6em;
    line-height: 1.2;
}

.card-summary {
    flex: 1 1 auto;
    overflow: hidden;
}

.inline-image {
    max-height: 24rem;
    object-fit: contain;
}

.card-rich-content :deep(img) {
    max-width: 100%;
    height: auto;
}
</style>
